import React from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Divider,
  Tooltip,
  IconButton,
  Avatar,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/SpaceDashboardOutlined";
import BusinessIcon from "@mui/icons-material/ApartmentOutlined";
import DevicesIcon from "@mui/icons-material/RouterOutlined";
import TicketIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import CloudOffIcon from "@mui/icons-material/CloudOffOutlined";
import LogsIcon from "@mui/icons-material/HistoryOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import ProfileIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import logo from "../../assets/logo.png";
import { APP_VERSION } from "../../version";
import { logoutThunk } from "../../features/auth/authSlice";

const navItems = [
  { label: "Search", to: "/search", icon: <SearchIcon fontSize="small" /> },
  { label: "Dashboard", to: "/dashboard", icon: <DashboardIcon fontSize="small" /> },
  { label: "Companies", to: "/companies", icon: <BusinessIcon fontSize="small" /> },
  { label: "Devices", to: "/devices", icon: <DevicesIcon fontSize="small" /> },
  { label: "Tickets", to: "/tickets", icon: <TicketIcon fontSize="small" /> },
  { label: "Offline", to: "/offline", icon: <CloudOffIcon fontSize="small" /> },
  { label: "Audit Log", to: "/audit-log", icon: <LogsIcon fontSize="small" /> },
  { label: "Profile", to: "/profile", icon: <ProfileIcon fontSize="small" /> },
];

const DrawerContent = ({ collapsed = false, onToggleCollapse, onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);

  const displayName = user?.name || user?.username || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async () => {
    onNavigate?.();
    await dispatch(logoutThunk());
    toast.success("Logged out");
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Top — logo + signed-in user */}
      <Toolbar
        variant="dense"
        sx={{
          minHeight: "60px !important",
          px: collapsed ? 0 : 2,
          display: "flex",
          flexDirection: collapsed ? "column" : "row",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: collapsed ? 0.5 : 0,
          py: collapsed ? 1 : 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            flex: collapsed ? "0 0 auto" : "1 1 auto",
          }}
        >
          {collapsed ? (
            <Tooltip title={displayName} placement="right">
              <Avatar
                sx={{
                  background:
                    "linear-gradient(135deg, #0d9488 0%, #0e7490 100%)",
                  color: "#fff",
                  width: 36,
                  height: 36,
                  fontSize: "0.8125rem",
                  fontWeight: 700,
                }}
              >
                {initials}
              </Avatar>
            </Tooltip>
          ) : (
            <>
              <Box
                component="img"
                src={logo}
                alt="Jothi"
                sx={{
                  width: 32,
                  height: 32,
                  objectFit: "contain",
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.15,
                  }}
                  noWrap
                >
                  {displayName}
                </Typography>
              </Box>
            </>
          )}
        </Box>
        {/* Collapse / Expand toggle — desktop only */}
        {onToggleCollapse && (
          <Tooltip
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            placement="right"
          >
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                backgroundColor: "action.hover",
                flexShrink: 0,
                "&:hover": {
                  backgroundColor: "primary.light",
                  color: "primary.main",
                },
              }}
            >
              {collapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />

      {/* Main nav */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1.5 }}>
        <List dense disablePadding>
          {navItems.map((item) => {
            const active =
              location.pathname === item.to ||
              location.pathname.startsWith(item.to + "/");
            const button = (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                selected={active}
                onClick={onNavigate}
                sx={{
                  px: collapsed ? 0 : 2,
                  justifyContent: collapsed ? "center" : "flex-start",
                  minHeight: 40,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 36,
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 600 : 500,
                    }}
                  />
                )}
              </ListItemButton>
            );
            return collapsed ? (
              <Tooltip title={item.label} placement="right" key={item.to}>
                {button}
              </Tooltip>
            ) : (
              button
            );
          })}

          {/* Logout — action item, not a route */}
          {(() => {
            const button = (
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  px: collapsed ? 0 : 2,
                  justifyContent: collapsed ? "center" : "flex-start",
                  minHeight: 40,
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "error.light",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: collapsed ? 0 : 36,
                    justifyContent: "center",
                    color: "error.main",
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                )}
              </ListItemButton>
            );
            return collapsed ? (
              <Tooltip title="Logout" placement="right" key="logout">
                {button}
              </Tooltip>
            ) : (
              <Box key="logout">{button}</Box>
            );
          })()}
        </List>
      </Box>

      {!collapsed && (
        <Box
          sx={{
            mx: 1,
            mb: 1,
            p: 1.25,
            borderRadius: "10px",
            background:
              "linear-gradient(135deg, rgba(13,148,136,0.10) 0%, rgba(34,211,238,0.10) 100%)",
            border: "1px solid",
            borderColor: "primary.light",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                background:
                  "linear-gradient(135deg, #0d9488 0%, #0e7490 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0.5,
                flexShrink: 0,
                boxShadow: "0 2px 6px -2px rgba(13,148,136,0.5)",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Jothi"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  lineHeight: 1.15,
                  color: "primary.main",
                  letterSpacing: "-0.005em",
                }}
                noWrap
              >
                Jothi Engineering
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "0.5625rem",
                  letterSpacing: "0.08em",
                  fontWeight: 600,
                  lineHeight: 1.1,
                  textTransform: "uppercase",
                }}
              >
                Admin Console
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: "1px solid",
              borderColor: "primary.light",
              pt: 0.5,
              mt: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.5625rem",
                color: "text.disabled",
                letterSpacing: "0.04em",
              }}
            >
              © {new Date().getFullYear()}
            </Typography>
            <Box
              sx={{
                px: 0.75,
                py: 0.125,
                borderRadius: "4px",
                backgroundColor: "primary.main",
                color: "#fff",
                fontSize: "0.5625rem",
                fontWeight: 700,
                fontFamily: "monospace",
                letterSpacing: "0.04em",
              }}
            >
              v{APP_VERSION}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default function Sidebar({
  drawerWidth,
  expandedWidth,
  mobileOpen,
  onClose,
  collapsed = false,
  onToggleCollapse,
}) {
  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: expandedWidth || drawerWidth },
        }}
      >
        <DrawerContent collapsed={false} onNavigate={onClose} />
      </Drawer>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: (t) =>
              t.transitions.create("width", {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.shorter,
              }),
          },
        }}
      >
        <DrawerContent collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      </Drawer>
    </Box>
  );
}
